import { FC, useEffect, useRef, useState } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import styled from 'styled-components';

import { useInboxActions } from '@/entities/inbox/hooks/useInboxActions';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { BtnClose } from '../../components/BtnClose';
import { useModalsStore } from '../../modals.store';
import { InboxEmailInfoProps } from '../../types';

import { FileItem } from './FileItem';
import { Actions, Body, Container, FileList, Header, Info, StyledDrawer, Title } from './styled';

const noSubject = 'No subject';
// TODO modal container RightSide
export const InboxEmailInfoModal: FC<InboxEmailInfoProps> = ({
  emailId,
  onRemove,
  onDownload,
  onAttach,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'inboxEmailInfo' });
  const modals = useModalsStore();
  const sjdb = useSJDatabase();
  const actions = useInboxActions();

  const [showBtnMore, setShowBtnMore] = useState(false);
  const [showMore, toggleShowMore] = useToggle(false);
  const refBody = useRef<HTMLDivElement | null>(null);

  const onUserClose = () => modals.closeModal();

  // TODO hack, span has clientHeight === 0 and scrollHeight === 0
  useEffect(() => {
    setTimeout(() => {
      if (refBody.current && refBody.current.clientHeight < refBody.current.scrollHeight) {
        setShowBtnMore(true);
      }
    }, 100);
  }, []);

  const email = sjdb.collections.inbox?.findById(emailId);

  if (!email) return null;

  const handleRemove = () => {
    actions.removeEmail(email, onRemove);
  };

  const handleAttach = () => {
    onUserClose();
    onAttach?.();
  };

  const handlePreviewFile = (index: number) =>
    actions.openEmailFilePreview(email, index, Boolean(onAttach));
  const handleDownloadFile = (index: number) => actions.downloadEmailFiles(email, [index]);
  const handleAttachFile = (index: number) => actions.attachEmailFiles(email, [index]);
  const handleEditFileComment = (index: number) => actions.openEditEmailFileComment(email, index);
  const handleRemoveFile = (index: number) => actions.removeEmailFile(email, index);

  return (
    <StyledDrawer width={600} footer={null} closable={false} onClose={onUserClose} open>
      <Container>
        <Header>
          <Title style={{ maxWidth: '80%' }} className="truncate" size={22} bold>
            {email.emailSubject || noSubject}
          </Title>
          <BtnClose onClick={onUserClose} style={{ position: 'absolute', right: 24, top: 24 }} />
        </Header>
        <Body style={{ marginRight: 4 }} className="uikitScrollbar">
          <Title size={16} bold>
            {t('general')}
          </Title>
          <Info>
            <div style={{ color: COLORS.systemDark200 }}>{t('title')}</div>
            <div className="lineclamp2">{email.emailSubject || noSubject}</div>
            <div style={{ color: COLORS.systemDark200 }}>{t('description')}</div>
            <div>
              <div className={cn({ lineclamp3: !showMore })} ref={refBody}>
                {email.emailBody || '-'}
              </div>
              {showBtnMore && (
                <Button type="text" onClick={toggleShowMore}>
                  {t('btnMore')}
                  <Icon name={showMore ? 'caret-up' : 'caret-down'} size="s20" />
                </Button>
              )}
            </div>
          </Info>
          <Title size={16} bold>
            {t('attachments', { count: email.attachments.length })}
          </Title>
          <FileList>
            {email.attachments.map((item, index) => (
              <FileItem
                fileInfo={item}
                index={index}
                onDetails={() => handlePreviewFile(index)}
                onDownload={() => handleDownloadFile(index)}
                onAttach={() => handleAttachFile(index)}
                onEditComment={() => handleEditFileComment(index)}
                onRemove={() => handleRemoveFile(index)}
                key={index}
              />
            ))}
          </FileList>
        </Body>
        <Actions>
          {
            <BtnRemove type="text" onClick={handleRemove}>
              {t('btnDelete')}
            </BtnRemove>
          }
          <div style={{ display: 'flex', gap: 16, flex: 1, justifyContent: 'flex-end' }}>
            {email.attachments.length > 0 && (
              <Button type="default" onClick={onDownload}>
                {t('btnDownload')}
              </Button>
            )}
            {onAttach && <Button onClick={handleAttach}>{t('btnAttach')}</Button>}
          </div>
        </Actions>
      </Container>
    </StyledDrawer>
  );
};

const BtnRemove = styled(Button)`
  &&&.text {
    color: ${COLORS.systemFlame200} !important;
  }

  &&&.text:hover {
    color: ${COLORS.systemFlame200} !important;
  }
`;
